import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

export interface InfraInfo {
  infra_provider: string;
  is_onprem: boolean;
}

export type EnvVarState = 'not_defined' | 'set_empty' | 'set';

export interface EnvVarStatus {
  name: string;
  required: boolean;
  is_set: boolean;
  // Tri-state: 'not_defined' (None/missing), 'set_empty' (defined but empty),
  // 'set' (has a value). 'is_set' is true only for 'set'.
  state: EnvVarState;
  derived?: boolean;
}

export interface EnvVarGroup {
  name: string;
  variables: EnvVarStatus[];
}

export interface EnvironmentCheck {
  infra_provider: string;
  is_onprem: boolean;
  all_ok: boolean;
  groups: EnvVarGroup[];
}

export type KeyVaultAvailability =
  | 'available'      // reachable over the network
  | 'unavailable'    // on-prem but the probe failed (DNS/refused/timeout)
  | 'not_configured' // on-prem but HASHICORP_VAULT_URL is empty
  | 'not_applicable'; // not an on-prem environment

export type KeyVaultSealState =
  | 'sealed'
  | 'unsealed'
  | 'unknown'         // on-prem but could not be determined (Vault unreachable)
  | 'not_applicable'; // cloud deployment (no seal concept)

export interface KeyVaultStatus {
  availability: KeyVaultAvailability;
  seal_state: KeyVaultSealState;
  // Concise, admin-friendly reason (never a raw stack). null when nominal.
  detail: string | null;
}

export interface InfraStatus {
  is_onprem: boolean;
  provider: string;
  keyvault: KeyVaultStatus;
}

export const environmentConfigApi = createApi({
  reducerPath: 'environmentConfigApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['EnvironmentCheck', 'InfraStatus'],
  endpoints: (builder) => ({
    getInfraInfo: builder.query<InfraInfo, void>({
      query: () => 'environment/info/',
    }),
    getEnvironmentCheck: builder.query<EnvironmentCheck, void>({
      query: () => 'environment/check/',
      providesTags: ['EnvironmentCheck'],
    }),
    getInfraStatus: builder.query<InfraStatus, void>({
      query: () => 'environment/infra-status/',
      providesTags: ['InfraStatus'],
    }),
  }),
});

export const {
  useGetInfraInfoQuery,
  useGetEnvironmentCheckQuery,
  useGetInfraStatusQuery,
} = environmentConfigApi;
